import { setupGoogleMapsInterceptor } from './googleMapsInterceptor';

// Set up interceptor immediately when this module loads
setupGoogleMapsInterceptor();

let isGoogleMapsLoaded = false;
let loadPromise: Promise<void> | null = null;

/**
 * Centralized Google Maps API loader that prevents multiple script loads
 * @returns Promise<void> - Resolves when Google Maps API is loaded
 */
export const loadGoogleMapsAPI = async (): Promise<void> => {
  // If already loaded, return immediately
  if (isGoogleMapsLoaded && window.google && window.google.maps) {
    return Promise.resolve();
  }

  // If already loading, return the existing promise
  if (loadPromise) {
    return loadPromise;
  }

  // Create new loading promise
  loadPromise = new Promise<void>((resolve, reject) => {
    // Check if already loaded (double-check)
    if (window.google && window.google.maps) {
      isGoogleMapsLoaded = true;
      loadPromise = null;
      resolve();
      return;
    }

    // Load script from backend proxy (no API key exposed to frontend)
    const script = document.createElement("script");
    script.src = `/api/google-maps/js-api/?libraries=drawing,places,geometry`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      isGoogleMapsLoaded = true;
      loadPromise = null;
      console.log("Google Maps API loaded successfully via backend proxy");
      
      resolve();
    };

    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Google Maps API via backend proxy"));
    };

    // Add script to document
    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Check if Google Maps API is already loaded
 * @returns boolean
 */
export const isGoogleMapsAPILoaded = (): boolean => {
  return isGoogleMapsLoaded && !!(window.google && window.google.maps);
}; 